import React, { createContext, useContext, ReactNode } from 'react';
import {
    IPrompt,
    TActiveChatType,
    TAIFeatureTypes,
    TPromptsTag
} from '../packages/ai-chat/types'; // Import necessary types
import { observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { boundMethod } from 'autobind-decorator';
import { guid } from '@honeybook/hbui/dist/utils/shared.utils';
import { IPromptMetadataResponse } from '~/packages/ai-core/core/ai-contextual-prompts.adapter';

export enum ChatViewState {
    HIDDEN = 'HIDDEN',
    CONVERSATIONS_LIST = 'CONVERSATIONS_LIST',
    CHAT_MESSAGES = 'CHAT_MESSAGES'
}

interface QueuedMessage {
    temp_id: string;
    feature: TAIFeatureTypes;
    prompt: IPrompt;
    conversationId?: string;
}

class GlobalAIChatStore {
    @observable activeChatType: TActiveChatType = null;
    @observable queuedMessage: QueuedMessage | null = null;
    @observable shouldFocusOnOpen: boolean = true;
    @observable displayConversationsList: boolean = false;

    @observable promptsByTag: Record<
        TPromptsTag,
        IPromptMetadataResponse | null
    > = {} as Record<TPromptsTag, IPromptMetadataResponse | null>;

    @action.bound
    setPromptsByTag(tag: TPromptsTag, prompts: IPromptMetadataResponse | null) {
        if (prompts) {
            this.promptsByTag[tag] = prompts;
        } else {
            delete this.promptsByTag[tag];
        }
    }

    private getChatType(feature: TAIFeatureTypes): TActiveChatType {
        if (!feature) {
            return null;
        }
        return feature === 'onboarding_chat' ? feature : 'business_partner';
    }

    @action.bound
    openChat(feature: TAIFeatureTypes, shouldFocus: boolean = true) {
        this.activeChatType = this.getChatType(feature);
        this.shouldFocusOnOpen = shouldFocus;
    }

    @action.bound
    closeChat() {
        this.activeChatType = null;
    }

    @action.bound
    sendMessageFromGlobal({
        feature,
        prompt
    }: {
        feature: TAIFeatureTypes;
        prompt: IPrompt;
    }) {
        if (this.displayConversationsList) {
            this.setDisplayConversationsList(false);
        }
        this.openChat(feature, true);
        this.queuedMessage = {
            temp_id: guid(),
            feature,
            prompt
        };
    }

    @action.bound
    clearQueuedMessage() {
        this.queuedMessage = null;
    }

    @action.bound
    setDisplayConversationsList(display: boolean) {
        this.displayConversationsList = display;
    }

    @boundMethod
    isChatFeatureVisible(feature: TAIFeatureTypes): boolean {
        return this.activeChatType === this.getChatType(feature);
    }

    @computed
    get isAnyChatVisible(): boolean {
        return this.activeChatType !== null;
    }
}

const globalAIChatStore = new GlobalAIChatStore();

// for testing purposes
export const getGlobalAIChatStore = () => {
    return globalAIChatStore;
};

const GlobalAIChatContext = createContext<GlobalAIChatStore>(globalAIChatStore);

interface GlobalAIChatProviderProps {
    children: ReactNode;
}

export const GlobalAIChatProvider = observer(
    ({ children }: GlobalAIChatProviderProps) => {
        return (
            <GlobalAIChatContext.Provider value={globalAIChatStore}>
                {children}
            </GlobalAIChatContext.Provider>
        );
    }
);

export const useGlobalAIChat = () => {
    const context = useContext(GlobalAIChatContext);
    if (!context) {
        throw new Error(
            'useGlobalAIChat must be used within a GlobalAIChatProvider'
        );
    }

    return context;
};
